import React, { useMemo, useState } from "react";
import AdminHeader from "../../components/layout/AdminHeader";
import RecordDetailCard from "../../features/data-view/components/RecordDetailCard";
import pubmedConfig from "../../features/data-view/config/datasets/pubmedConfig";
import { buildDetailModel } from "../../features/data-view/utils/recordMappers";
import adminPubMedIngestService from "../../services/adminPubMedIngestService";

const getErrorMessage = (error, fallback) =>
  error?.data?.message || error?.message || fallback;

const AdminPubMedIngest = () => {
  const [pmid, setPmid] = useState("");
  const [previewPayload, setPreviewPayload] = useState(null);
  const [ingestPayload, setIngestPayload] = useState(null);
  const [error, setError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isIngesting, setIsIngesting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const activePayload = ingestPayload || previewPayload;
  const activeRecord = activePayload?.editable_record || activePayload?.record || null;
  const detailModel = useMemo(
    () => buildDetailModel(activeRecord, pubmedConfig),
    [activeRecord]
  );

  const resetResultState = () => {
    setPreviewPayload(null);
    setIngestPayload(null);
    setStatusMessage("");
  };

  const handlePmidChange = (event) => {
    setPmid(event.target.value);
    setError("");
  };

  const handlePreview = async (event) => {
    event.preventDefault();
    setIsPreviewing(true);
    setError("");
    resetResultState();

    try {
      const response = await adminPubMedIngestService.preview(pmid);
      if (response.status === "error") {
        setError(response.message || "Unable to fetch this PMID.");
        return;
      }
      setPreviewPayload(response);
    } catch (previewError) {
      setError(getErrorMessage(previewError, "Unable to fetch this PMID."));
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleIngest = async () => {
    const targetPmid = activePayload?.pmid || pmid;
    setIsIngesting(true);
    setError("");
    setStatusMessage("");

    try {
      const response = await adminPubMedIngestService.ingest(targetPmid);
      if (response.status === "error") {
        setError(response.message || "Unable to ingest this PMID.");
        return;
      }
      setIngestPayload(response);
      setStatusMessage(response.message || "PubMed record ingested.");
    } catch (ingestError) {
      setError(getErrorMessage(ingestError, "Unable to ingest this PMID."));
    } finally {
      setIsIngesting(false);
    }
  };

  const handleDelete = async () => {
    if (!ingestPayload?.pmid || !ingestPayload?.deletion_allowed) {
      return;
    }

    setIsDeleting(true);
    setError("");
    setStatusMessage("");

    try {
      const response = await adminPubMedIngestService.delete(ingestPayload.pmid);
      if (response.status === "error") {
        setError(response.message || "Unable to delete this PubMed record.");
        return;
      }
      resetResultState();
      setPmid("");
      setStatusMessage("PubMed record deleted.");
    } catch (deleteError) {
      setError(getErrorMessage(deleteError, "Unable to delete this PubMed record."));
    } finally {
      setIsDeleting(false);
    }
  };

  const canConfirm = Boolean(previewPayload) && !ingestPayload;
  const canDelete = Boolean(ingestPayload?.deletion_allowed);

  return (
    <div className="min-h-screen bg-[#0b0f17] text-slate-100">
      <AdminHeader
        title="PubMed Ingest"
        subtitle="Fetch, preview, confirm, and safely remove admin-ingested PubMed records."
      />

      <main className="mx-auto flex w-full max-w-[1180px] flex-col gap-6 px-4 py-8 sm:px-6 lg:px-10">
        <section className="rounded-2xl border border-slate-800 bg-[#111827] p-5 shadow-[0_20px_50px_rgba(2,6,23,0.35)]">
          <form onSubmit={handlePreview} className="flex flex-col gap-4 md:flex-row md:items-end">
            <label className="flex-1">
              <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                PMID
              </span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={pmid}
                onChange={handlePmidChange}
                placeholder="12345"
                className="h-12 w-full rounded-xl border border-slate-700 bg-slate-950 px-4 text-sm text-slate-100 outline-none transition-all placeholder:text-slate-600 focus:border-sky-400/70 focus:ring-2 focus:ring-sky-500/20"
              />
            </label>
            <button
              type="submit"
              disabled={isPreviewing || isIngesting || isDeleting}
              className="h-12 rounded-xl border border-sky-400/40 bg-sky-500/15 px-5 text-sm font-semibold text-sky-100 transition-colors hover:border-sky-300 hover:bg-sky-500/20 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isPreviewing ? "Fetching..." : "Add / Fetch"}
            </button>
          </form>

          {error ? (
            <div className="mt-4 rounded-xl border border-rose-500/25 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
              {error}
            </div>
          ) : null}
          {statusMessage ? (
            <div className="mt-4 rounded-xl border border-emerald-500/25 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
              {statusMessage}
            </div>
          ) : null}
        </section>

        {detailModel ? (
          <div className="space-y-4">
            <div className="flex flex-wrap justify-end gap-3">
              {canConfirm ? (
                <button
                  type="button"
                  onClick={handleIngest}
                  disabled={isIngesting || isDeleting}
                  className="rounded-xl border border-emerald-400/40 bg-emerald-500/15 px-4 py-2.5 text-sm font-semibold text-emerald-100 transition-colors hover:border-emerald-300 hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isIngesting ? "Ingesting..." : "Confirm Ingestion"}
                </button>
              ) : null}
              {canDelete ? (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="rounded-xl border border-rose-400/40 bg-rose-500/15 px-4 py-2.5 text-sm font-semibold text-rose-100 transition-colors hover:border-rose-300 hover:bg-rose-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isDeleting ? "Deleting..." : "Delete Admin Record"}
                </button>
              ) : null}
            </div>

            <RecordDetailCard
              datasetKey="pubmed"
              datasetLabel="PubMed"
              detailModel={detailModel}
              editableRecord={activeRecord}
              auditMetadata={activePayload?.audit_metadata}
              theme={pubmedConfig.theme}
              onClose={resetResultState}
              onDownload={() => {}}
              showDownload={false}
            />
          </div>
        ) : null}
      </main>
    </div>
  );
};

export default AdminPubMedIngest;
